import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/utils/cn'
import { Loader2, AlertCircle, Maximize2, Minimize2, RotateCcw, Volume2, VolumeX, X } from 'lucide-react'
import { SplatRenderer } from './splatv-renderer'
import { AxisHelper } from './AxisHelper'
import type { CameraState, AnimationKeyframe, ModelTransform } from '@/types/camera'
import type { Quaternion } from '@/utils/quaternion'
import { cameraStateToQuaternion } from '@/utils/cameraConversion'
import { useCameraAnimation } from '@/utils/cameraAnimation'
import { useXR } from '@/hooks/useXR'
import { XRButton } from '@/components/XRControls'
import { ARCameraBackground } from '@/components/ARCameraBackground'
import { ARCameraController } from '@/utils/ar-camera-controller'
// @ts-ignore - JS module
import { ARTouchController } from '@/lib/splatv/ar-touch-controller'

interface SplatViewerProps {
  modelUrl: string
  className?: string
  autoRotate?: boolean
  controls?: boolean
  quality?: 'low' | 'medium' | 'high'
  onLoad?: () => void
  onError?: (error: Error) => void
  onProgress?: (progress: number) => void
  onCanvasReady?: (canvas: HTMLCanvasElement) => void
  onCameraChange?: (position: [number, number, number], quaternion: Quaternion) => void
  cameraPosition?: [number, number, number]
  cameraQuaternion?: Quaternion
  defaultCamera?: CameraState
  animation?: {
    keyframes: AnimationKeyframe[]
    duration: number
    loop?: boolean
  }
  showUI?: boolean
  pauseAnimationOnInteraction?: boolean
  showAxes?: boolean
  controlMode?: 'orbit' | 'free'
  autoFocus?: boolean
  modelTransform?: ModelTransform
  xrEnabled?: boolean
  onXRSessionStart?: () => void
  onXRSessionEnd?: () => void
  xrSessionType?: 'vr' | 'ar' | 'auto'
  /** Optional looping soundtrack. Auto-plays on mount; falls back to muted
   *  autoplay if the browser blocks unmuted, then a Mute button lets the
   *  user unmute on a real click (which always grants user activation). */
  audioUrl?: string | null
}

export function SplatViewer({
  modelUrl,
  className,
  autoRotate = false,
  controls = true,
  quality = 'medium',
  onLoad,
  onError,
  onProgress,
  onCanvasReady,
  onCameraChange,
  cameraPosition,
  cameraQuaternion,
  defaultCamera,
  animation,
  showUI: showUIProp,
  pauseAnimationOnInteraction = false,
  showAxes = false,
  controlMode = 'orbit',
  autoFocus = true,
  modelTransform,
  xrEnabled = false,
  onXRSessionStart,
  onXRSessionEnd,
  xrSessionType = 'auto',
  audioUrl = null,
}: SplatViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isMuted, setIsMuted] = useState(false)
  // True while the soundtrack is only playing because we fell back to muted
  // autoplay (browser blocked unmuted). Lets the first user gesture unmute it.
  const autoMutedRef = useRef(false)
  const rendererRef = useRef<SplatRenderer | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [fps, setFps] = useState(0)
  const [showUI, setShowUI] = useState(showUIProp ?? true)
  const [currentCameraQuaternion, setCurrentCameraQuaternion] = useState<Quaternion>({ w: 1, x: 0, y: 0, z: 0 })
  const [currentControlMode, setCurrentControlMode] = useState<'orbit' | 'free'>(controlMode)
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null)
  const pauseRef = useRef<(() => void) | null>(null)
  const playRef = useRef<(() => void) | null>(null)
  const isPlayingRef = useRef(false)
  const isInteractingRef = useRef(false)
  const arCameraControllerRef = useRef<ARCameraController | null>(null)
  const arTouchControllerRef = useRef<any>(null)
  const [isARMode, setIsARMode] = useState(false)

  // XR hook with fallback support
  const xr = useXR({
    onSessionStart: onXRSessionStart,
    onSessionEnd: onXRSessionEnd,
    sessionType: xrSessionType,
    enableFallback: true
  })

  // Camera animation hook
  const { currentCamera, play, pause, isPlaying } = useCameraAnimation(
    animation?.keyframes || [],
    animation?.duration || 0,
    animation?.loop,
    !!animation && !isLoading
  )
  
  // Update refs to avoid stale closures
  useEffect(() => {
    pauseRef.current = pause
    playRef.current = play
    isPlayingRef.current = isPlaying
  }, [pause, play, isPlaying])

  // Audio: auto-play the soundtrack on mount. Browsers block unmuted autoplay
  // without a prior user gesture, but muted autoplay is always allowed — fall
  // back to that and surface the muted state so the user can unmute with a
  // click (which always grants user activation).
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !audioUrl) return
    autoMutedRef.current = false
    audio.play().catch(() => {
      audio.muted = true
      setIsMuted(true)
      autoMutedRef.current = true
      audio.play().catch(() => { /* even muted failed; give up on audio */ })
    })
  }, [audioUrl])

  // If unmuted autoplay was blocked, unmute on the first user gesture anywhere
  // on the page (a click/tap/key grants audio activation). The user interacts
  // with the viewer to orbit anyway, so the soundtrack starts as soon as they do.
  useEffect(() => {
    if (!audioUrl) return
    const unmuteOnGesture = () => {
      const audio = audioRef.current
      if (!audio) return
      if (autoMutedRef.current) {
        audio.muted = false
        setIsMuted(false)
        autoMutedRef.current = false
        if (audio.paused) audio.play().catch(() => {})
      }
      remove()
    }
    const opts = { capture: true } as const
    const remove = () => {
      window.removeEventListener('pointerdown', unmuteOnGesture, opts)
      window.removeEventListener('keydown', unmuteOnGesture, opts)
      window.removeEventListener('touchstart', unmuteOnGesture, opts)
    }
    window.addEventListener('pointerdown', unmuteOnGesture, opts)
    window.addEventListener('keydown', unmuteOnGesture, opts)
    window.addEventListener('touchstart', unmuteOnGesture, opts)
    return remove
  }, [audioUrl])

  const toggleMute = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.muted = !audio.muted
    setIsMuted(audio.muted)
    // Safety net: the muted-autoplay fallback can fail silently (some
    // browsers reject back-to-back play() calls when the first one is still
    // settling its rejection). The click is a real user gesture, so kick
    // playback off now if the element is still paused.
    if (audio.paused) {
      audio.play().catch(() => {})
    }
  }, [])

  useEffect(() => {
    if (!canvasRef.current) return

    const canvas = canvasRef.current
    const renderer = new SplatRenderer(canvas, {
      quality,
      autoRotate: autoRotate && !animation, // Disable auto-rotate if animation is playing
      controls: controls, // Always enable controls if requested
      defaultCamera: defaultCamera,
      controlMode: controlMode,
      autoFocus: autoFocus
    })

    rendererRef.current = renderer

    // Set up event listeners
    renderer.on('load', () => {
      setIsLoading(false)
      
      // Apply model transform if provided
      if (modelTransform) {
        renderer.setModelTransform(modelTransform)
      }
      
      // Start animation if provided
      if (animation) {
        play()
      }
      
      onLoad?.()
    })

    renderer.on('error', (...args: unknown[]) => {
      const err = args[0] as Error
      setError(err.message)
      setIsLoading(false)
      onError?.(err)
    })

    renderer.on('progress', (...args: unknown[]) => {
      const prog = args[0] as number
      setProgress(prog)
      onProgress?.(prog)
    })

    renderer.on('fps', (...args: unknown[]) => {
      const currentFps = args[0] as number
      setFps(currentFps)
    })
    
    // Set up control mode change listener
    renderer.on('controlModeChanged', (...args: unknown[]) => {
      const mode = args[0] as 'orbit' | 'free'
      setCurrentControlMode(mode)
    })
    
    // Set up camera change listener
    renderer.on('cameraChange', (...args: unknown[]) => {
      const position = args[0] as [number, number, number]
      const quaternion = args[1] as Quaternion
      
      // Update local state for axis helper
      setCurrentCameraQuaternion(quaternion)
      
      // Call external callback if provided
      onCameraChange?.(position, quaternion)
    })
    
    // Get initial control mode
    const initialMode = renderer.getControlMode()
    setCurrentControlMode(initialMode)


    // Load the model
    renderer.loadModel(modelUrl)

    // Store renderer reference on canvas for external access
    ;(canvas as any).__splatRenderer = renderer
    
    // Notify parent about canvas ready
    if (onCanvasReady) {
      onCanvasReady(canvas)
    }

    // Handle resize. Prefer offsetWidth/offsetHeight (layout dimensions) over
    // getBoundingClientRect (which reflects CSS transforms). This matters when
    // the viewer lives inside an animated container — e.g. framer-motion's
    // `scale` animation — because getBoundingClientRect captures the scaled
    // size during the transition and leaves the canvas pixel buffer the wrong
    // size once the transform settles, producing a misaligned render.
    const handleResize = () => {
      const parent = canvas.parentElement
      if (parent && renderer) {
        const width = Math.floor(parent.offsetWidth)
        const height = Math.floor(parent.offsetHeight)

        // Only resize if dimensions actually changed
        if (canvas.width !== width * window.devicePixelRatio ||
            canvas.height !== height * window.devicePixelRatio) {
          canvas.width = width * window.devicePixelRatio
          canvas.height = height * window.devicePixelRatio
          canvas.style.width = `${width}px`
          canvas.style.height = `${height}px`
          renderer.resize(canvas.width, canvas.height)
        }
      }
    }
    
    // Handle fullscreen changes
    const handleFullscreenChange = () => {
      // Delay resize to ensure DOM has updated
      setTimeout(() => {
        handleResize()
      }, 100)
    }

    // Initial resize after renderer is created
    const parentEl = canvas.parentElement
    if (parentEl) {
      const w = parentEl.offsetWidth
      const h = parentEl.offsetHeight
      canvas.width = w * window.devicePixelRatio
      canvas.height = h * window.devicePixelRatio
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      renderer.resize(canvas.width, canvas.height)
    }
    
    // Force a resize after a short delay to ensure proper sizing
    setTimeout(() => {
      handleResize()
    }, 100)
    
    // Use ResizeObserver for more reliable size updates
    const resizeObserver = new ResizeObserver((entries) => {
      // Only handle resize if there's an actual size change
      for (const entry of entries) {
        if (entry.target === canvas.parentElement) {
          handleResize()
        }
      }
    })
    
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement)
    }
    
    // Store observer reference for fullscreen handling
    ;(canvas as any).__resizeObserver = resizeObserver

    window.addEventListener('resize', handleResize)
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    
    // Add keyboard event listener for H and O keys
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'h' || e.key === 'H') {
        setShowUI(prev => !prev)
      }
      if (e.key === 'o' || e.key === 'O') {
        if (renderer) {
          renderer.toggleControlMode()
        }
      }
    }
    window.addEventListener('keydown', handleKeyPress)
    

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('keydown', handleKeyPress)
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      
      resizeObserver.disconnect()
      renderer.removeAllListeners('controlModeChanged')
      renderer.removeAllListeners('cameraChange')
      renderer.dispose()
    }
  }, [modelUrl, quality, autoRotate, controls, defaultCamera, animation]) // Remove callback dependencies to prevent re-initialization

  // Separate effect for interaction handlers
  useEffect(() => {
    if (!canvasRef.current || !pauseAnimationOnInteraction || !animation) return
    
    const canvas = canvasRef.current
    
    const handleMouseEnter = () => {
      isInteractingRef.current = true
      if (isPlayingRef.current) {
        pauseRef.current?.()
        
        // Clear any existing timer
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current)
          inactivityTimerRef.current = null
        }
      }
    }
    
    const handleMouseLeave = () => {
      // Start inactivity timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
      
      inactivityTimerRef.current = setTimeout(() => {
        isInteractingRef.current = false
        playRef.current?.()
        inactivityTimerRef.current = null
      }, 3000) // 3 seconds delay
    }
    
    // Add hover event listeners
    canvas.addEventListener('mouseenter', handleMouseEnter)
    canvas.addEventListener('mouseleave', handleMouseLeave)
    
    return () => {
      // Remove hover listeners
      canvas.removeEventListener('mouseenter', handleMouseEnter)
      canvas.removeEventListener('mouseleave', handleMouseLeave)
      
      // Clear inactivity timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
      
      // Reset interaction state
      isInteractingRef.current = false
    }
  }, [pauseAnimationOnInteraction, animation])

  // Store previous camera values to detect actual changes
  const prevCameraRef = useRef<{ position?: [number, number, number], quaternion?: Quaternion }>({})
  
  // Update camera position when props change
  useEffect(() => {
    if (rendererRef.current && cameraPosition && cameraQuaternion) {
      // Check if values actually changed (not just reference)
      const posChanged = !prevCameraRef.current.position || 
        cameraPosition.some((v, i) => Math.abs(v - prevCameraRef.current.position![i]) > 0.0001)
      const quatChanged = !prevCameraRef.current.quaternion ||
        Math.abs(cameraQuaternion.w - prevCameraRef.current.quaternion.w) > 0.0001 ||
        Math.abs(cameraQuaternion.x - prevCameraRef.current.quaternion.x) > 0.0001 ||
        Math.abs(cameraQuaternion.y - prevCameraRef.current.quaternion.y) > 0.0001 ||
        Math.abs(cameraQuaternion.z - prevCameraRef.current.quaternion.z) > 0.0001
      
      if (posChanged || quatChanged) {
        rendererRef.current.setCameraWithQuaternion(cameraPosition, cameraQuaternion)
        prevCameraRef.current = { position: [...cameraPosition], quaternion: { ...cameraQuaternion } }
      }
    }
  }, [cameraPosition, cameraQuaternion])

  // Update camera from animation (only when not interacting)
  useEffect(() => {
    if (rendererRef.current && animation && currentCamera && !isInteractingRef.current) {
      const { position, quaternion } = cameraStateToQuaternion(currentCamera)
      rendererRef.current.setCameraWithQuaternion(position, quaternion)
    }
  }, [currentCamera, animation])
  
  // Handle device orientation in AR mode
  useEffect(() => {
    const isActive = xr.state.isActive && xr.state.sessionType === 'device-orientation-ar'
    setIsARMode(isActive)
    
    if (!isActive) return
    
    const deviceOrientationAR = xr.getDeviceOrientationAR()
    if (!deviceOrientationAR || !rendererRef.current || !canvasRef.current) return
    
    // Create AR camera controller if not exists
    if (!arCameraControllerRef.current) {
      arCameraControllerRef.current = new ARCameraController()
    }
    
    // Create AR touch controller if not exists
    if (!arTouchControllerRef.current) {
      arTouchControllerRef.current = new ARTouchController(canvasRef.current, rendererRef.current)
    }
    
    const arController = arCameraControllerRef.current
    const arTouchController = arTouchControllerRef.current
    
    // Enable world mode with defaultCamera as baseline
    arController.setWorldMode(true, defaultCamera)
    
    // Enable movement tracking (experimental)
    // This allows the user to walk around in AR space
    arController.setMovementTracking(true)
    arController.setMovementScale(0.1) // Adjust sensitivity as needed
    
    // Enable AR touch controls
    arTouchController.enable()
    
    // Disable normal camera controls
    if (rendererRef.current.cameraControls) {
      rendererRef.current.cameraControls.enabled = false
    }
    
    // Handle orientation changes
    const handleOrientationChange = (orientation: { alpha: number; beta: number; gamma: number }) => {
      arController.updateOrientation(orientation)
      
      // Get camera transform
      const transform = arController.getCameraTransform(window.orientation || 0)
      
      // Apply to renderer
      if (rendererRef.current) {
        // In world mode, camera position is fixed relative to model
        // Only rotation changes based on device orientation
        // Convert rotation to quaternion (simplified for now)
        const quat: Quaternion = {
          w: 1, // This would need proper conversion from euler angles
          x: 0,
          y: 0,
          z: 0
        }
        rendererRef.current.setCameraWithQuaternion(
          [transform.position.x, transform.position.y, transform.position.z],
          quat
        )
      }
    }
    
    // Handle motion changes
    const handleMotionChange = (motion: {
      acceleration: { x: number; y: number; z: number }
      rotationRate: { alpha: number; beta: number; gamma: number }
    }) => {
      arController.updateMotion(motion)
    }
    
    // Subscribe to events
    deviceOrientationAR.on('orientationChange', handleOrientationChange as any)
    deviceOrientationAR.on('motionChange', handleMotionChange as any)
    
    // Handle double tap
    const handleDoubleTap = () => {
      // Exit AR on double tap
      if (xr.state.isActive) {
        xr.exitXR()
      } else {
        // Otherwise calibrate
        arController.calibrate()
      }
    }
    canvasRef.current?.addEventListener('dblclick', handleDoubleTap)
    
    return () => {
      deviceOrientationAR.off('orientationChange', handleOrientationChange as any)
      deviceOrientationAR.off('motionChange', handleMotionChange as any)
      canvasRef.current?.removeEventListener('dblclick', handleDoubleTap)
      
      // Reset controllers
      arController.reset()
      arController.setWorldMode(false)
      
      // Disable AR touch controls
      if (arTouchControllerRef.current) {
        arTouchControllerRef.current.disable()
      }
      
      // Re-enable normal camera controls
      if (rendererRef.current?.cameraControls) {
        rendererRef.current.cameraControls.enabled = true
      }
    }
  }, [xr.state.isActive, xr.state.sessionType, xr])
  
  // Handle fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement
      setIsFullscreen(isCurrentlyFullscreen)
      
      // Force multiple resize attempts to handle different timing scenarios
      const resizeAttempts = [50, 150, 300, 500]
      resizeAttempts.forEach(delay => {
        setTimeout(() => {
          if (canvasRef.current && rendererRef.current) {
            const canvas = canvasRef.current
            const parent = canvas.parentElement
            
            if (parent) {
              // Clear existing size to force recalculation
              canvas.style.width = ''
              canvas.style.height = ''
              
              // Force layout recalculation
              parent.offsetHeight // Force reflow
              
              const rect = parent.getBoundingClientRect()
              
              // For modal contexts, ensure we're getting the actual container size
              const width = Math.floor(rect.width)
              const height = Math.floor(rect.height)
              
              // Set canvas internal resolution
              canvas.width = width * window.devicePixelRatio
              canvas.height = height * window.devicePixelRatio
              
              // Set canvas display size
              canvas.style.width = `${width}px`
              canvas.style.height = `${height}px`
              
              // Update renderer
              rendererRef.current.resize(canvas.width, canvas.height)
              
            }
          }
        }, delay)
      })
    }
    
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [])


  const toggleFullscreen = () => {
    if (!canvasRef.current?.parentElement) return

    if (!isFullscreen) {
      canvasRef.current.parentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen().then(() => {
        // Force resize after exiting fullscreen with multiple attempts
        const resizeAttempts = [50, 150, 300, 500, 750, 1000]
        resizeAttempts.forEach(delay => {
          setTimeout(() => {
            if (canvasRef.current && rendererRef.current) {
              const canvas = canvasRef.current
              const parent = canvas.parentElement
              
              if (parent) {
                // Clear canvas size to force recalculation
                canvas.style.width = ''
                canvas.style.height = ''
                canvas.width = 0
                canvas.height = 0
                
                // Force layout recalculation
                parent.style.display = 'none'
                parent.offsetHeight // Force reflow
                parent.style.display = ''
                
                // Get fresh dimensions
                const rect = parent.getBoundingClientRect()
                const width = Math.floor(rect.width)
                const height = Math.floor(rect.height)
                
                // Set new dimensions
                canvas.width = width * window.devicePixelRatio
                canvas.height = height * window.devicePixelRatio
                canvas.style.width = `${width}px`
                canvas.style.height = `${height}px`
                
                // Update renderer
                rendererRef.current.resize(canvas.width, canvas.height)
                
              }
            }
          }, delay)
        })
      })
      setIsFullscreen(false)
    }
  }

  return (
    <div className={cn(
      'relative bg-gray-900 rounded-lg overflow-hidden',
      isARMode && 'ring-4 ring-green-500 ring-opacity-50',
      className
    )}>
      {/* AR Camera Background */}
      {xr.state.isActive && xr.state.sessionType === 'device-orientation-ar' && (
        <ARCameraBackground 
          deviceOrientationAR={xr.getDeviceOrientationAR()}
          className="z-0"
        />
      )}
      
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className={cn(
          "w-full h-full",
          xr.state.isActive && xr.state.sessionType === 'device-orientation-ar' && "relative z-10"
        )}
        style={{ touchAction: 'none' }}
      />

      {/* Optional soundtrack — loop so it tracks the visit length */}
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} loop preload="auto" />
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 backdrop-blur-sm">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
            <p className="text-white mb-2">Loading Model...</p>
            <div className="w-48 h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-400 mt-2">{Math.round(progress)}%</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 backdrop-blur-sm">
          <div className="text-center p-6">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-white mb-2">Failed to Load Model</p>
            <p className="text-sm text-gray-400">{error}</p>
          </div>
        </div>
      )}

      {/* AR Status Indicator - Only show when AR is active */}
      {!isLoading && !error && isARMode && (
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <div className="px-3 py-1 rounded-lg text-sm font-medium transition-all bg-green-600/80 text-white backdrop-blur-sm">
            🔴 AR Mode ON
          </div>
        </div>
      )}

      {/* Controls Overlay */}
      {!isLoading && !error && (
        <div className="absolute top-4 right-4 flex items-center gap-2">
          {/* Show regular controls when showUI is true */}
          {showUI && (
            <>
              {/* FPS Counter */}
              <div className="bg-gray-900/80 backdrop-blur-sm px-3 py-1 rounded-lg text-sm text-white">
                {fps} FPS
              </div>

              {/* Reset Camera Button */}
              <button
                onClick={() => rendererRef.current?.resetCamera()}
                className="p-2 bg-gray-900/80 backdrop-blur-sm rounded-lg text-white hover:bg-gray-800/80 transition-colors"
                aria-label="Reset camera"
                title="Reset camera to default view"
              >
                <RotateCcw size={20} />
              </button>

              {/* Fullscreen Button */}
              <button
                onClick={toggleFullscreen}
                className="p-2 bg-gray-900/80 backdrop-blur-sm rounded-lg text-white hover:bg-gray-800/80 transition-colors"
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </button>
            </>
          )}

          {/* Mute toggle — shown whenever there's a soundtrack, regardless
              of showUI, so audio can always be silenced. */}
          {audioUrl && (
            <button
              onClick={toggleMute}
              className="p-2 bg-gray-900/80 backdrop-blur-sm rounded-lg text-white hover:bg-gray-800/80 transition-colors"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          )}
          
          {/* XR Button - Always visible when XR is enabled */}
          {xrEnabled && (
            <>
              {!xr.state.isActive ? (
                showUI && (
                  <XRButton
                    onEnterXR={() => {
                      if (canvasRef.current) {
                        const sessionType = xr.state.isSupported && xrSessionType === 'auto' 
                          ? 'vr' 
                          : xrSessionType === 'ar' ? 'ar' : 'vr'
                        
                        if (sessionType === 'ar') {
                          xr.enterAR(canvasRef.current, modelUrl)
                        } else {
                          xr.enterVR(canvasRef.current, modelUrl)
                        }
                      }
                    }}
                    xrSupported={xr.state.isSupported}
                    sessionType={xrSessionType === 'ar' ? 'ar' : 'vr'}
                  />
                )
              ) : (
                // Exit button is ALWAYS visible when XR is active
                <button
                  onClick={() => xr.exitXR()}
                  className="p-2 bg-red-600/80 backdrop-blur-sm rounded-lg text-white hover:bg-red-700/80 transition-colors animate-pulse"
                  aria-label="Exit XR"
                >
                  <X size={20} />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Axes Helper */}
      {showAxes && !isLoading && !error && (
        <AxisHelper className="absolute bottom-4 right-2" />
      )}

      {/* Instructions */}
      {!isLoading && !error && controls && showUI && !isARMode && (
        <div className="absolute bottom-4 left-4 bg-gray-900/80 backdrop-blur-sm px-3 py-2 rounded-lg text-xs text-gray-300">
          <div className="space-y-1">
            <p>
              {currentControlMode === 'orbit' 
                ? 'Drag to rotate • Ctrl+drag to pan • Scroll to rotate • Ctrl+Scroll to zoom' 
                : 'Drag to rotate • Ctrl+drag to pan • Scroll to rotate • Ctrl+Scroll to zoom • WASD to move • Q/E to roll'
              }
            </p>
            <p>Shift+Scroll to pan • Arrow keys to rotate • O to switch mode • H to hide</p>
          </div>
        </div>
      )}
      
      {/* AR Mode Instructions - Always visible in AR mode */}
      {!isLoading && !error && isARMode && (
        <div className="absolute bottom-4 left-4 bg-gray-900/80 backdrop-blur-sm px-3 py-2 rounded-lg text-xs text-gray-300 max-w-md">
          <div className="space-y-1">
            <p className="font-semibold text-green-400">📱 AR Mode Active</p>
            <p>Move device to look around • Walk to move camera (experimental) • Single finger to move model • Pinch to scale • Two fingers to rotate • Double tap to reset</p>
            <p className="text-[10px] text-gray-500 mt-1">
              Session: {xr.state.sessionType} | Movement tracking: ON | {defaultCamera ? 'Using default camera as baseline' : 'Standard AR position'}
            </p>
          </div>
        </div>
      )}

      {/* Camera Mode Indicator */}
      {!isLoading && !error && controls && showUI && (
        <div className="absolute bottom-28 right-2 bg-gray-900/80 backdrop-blur-sm px-3 py-1 rounded-lg text-xs text-white font-medium">
          {currentControlMode === 'orbit' ? '🎯 Standard View' : '🎮 Free Movement'}
        </div>
      )}

      {/* 3D Axis Helper */}
      {!isLoading && !error && controls && showUI && (
        <AxisHelper 
          cameraQuaternion={currentCameraQuaternion} 
          className="bottom-4 right-2"
        />
      )}
    </div>
  )
}